import React, { useState, useEffect } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { ApplicationLearningSummary } from '../types';
import { AppPagination } from '../../../components/ui/AppPagination';

interface KanbanBoardProps {
  items: ApplicationLearningSummary[];
  loading: boolean;
  onDetails: (id: number) => void;
}

const COMPLETED_PAGE_SIZE = 10;

const getDaysPlural = (n: number) => {
  const absN = Math.abs(n);
  if (absN % 10 === 1 && absN % 100 !== 11) return 'день';
  if (absN % 10 >= 2 && absN % 10 <= 4 && (absN % 100 < 10 || absN % 100 >= 20)) return 'дня';
  return 'дней';
};

const KanbanCard: React.FC<{ item: ApplicationLearningSummary; onClick: () => void }> = ({ item, onClick }) => {
  const deptLogo = item.departments && item.departments.length > 0 ? item.departments[0] : null;
  const specName = item.specialist_full_name || 'Не назначен';
  const activeCount = Math.max(0, item.total_enrollments - item.completed_count - item.no_activity_count);

  const renderStats = () => {
    switch (item.overall_status) {
      case 'no_data':
        return (
          <div className="flex items-center gap-1.5 text-[12px] text-[#6B7280]">
            <div className="w-2 h-2 rounded-full bg-[#D1D5DB]" />
            <span>{item.no_activity_count} без акт.</span>
          </div>
        );
      case 'in_progress':
        return (
          <div className="flex items-center gap-4 text-[12px] text-[#6B7280]">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
              <span>{item.completed_count} заверш.</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#EAB308]" />
              <span>{activeCount} активн.</span>
            </div>
          </div>
        );
      case 'at_risk':
        return (
          <div className="flex items-center gap-4 text-[12px] text-[#6B7280]">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#F97316]" />
              <span>{item.at_risk_count} риск.</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
              <span>{item.completed_count} заверш.</span>
            </div>
          </div>
        );
      case 'overdue':
        return (
          <div className="flex items-center gap-4 text-[12px] text-[#6B7280]">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#EF4444]" />
              <span>{item.overdue_count} просроч.</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
              <span>{item.completed_count} заверш.</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderDeadline = () => {
    if (item.days_until_nearest_deadline === null) return null;
    const absDays = Math.abs(item.days_until_nearest_deadline);
    const daysText = getDaysPlural(absDays);

    let content = null;
    if (item.overall_status === 'overdue') {
      content = (
        <div className="flex items-center gap-1 text-[12px] font-medium text-[#EF4444]">
          <ErrorOutlineIcon style={{ fontSize: 16 }} />
          <span>Просрочено на {absDays} {daysText}.</span>
        </div>
      );
    } else if (item.overall_status === 'at_risk') {
      content = (
        <div className="flex items-center gap-1 text-[12px] font-medium text-[#F97316]">
          <AccessTimeIcon style={{ fontSize: 16 }} />
          <span>{absDays} {daysText} до дедлайна</span>
        </div>
      );
    }

    if (!content) return null;

    return (
      <div className="border-t border-[#E5E7EB] pt-3 mt-1">
        {content}
      </div>
    );
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl p-4 shadow-sm border border-[#E5E7EB] cursor-pointer hover:shadow-md hover:border-blue-300 transition-all flex flex-col gap-3 shrink-0"
    >
      <div className="flex items-center gap-2">
        {deptLogo && (
          <span className="font-semibold text-[12px] text-[#1E61AF] uppercase shrink-0">
            {deptLogo}
          </span>
        )}
        <span className="font-bold text-[12px] leading-tight text-[#1F2937] line-clamp-2">
          {item.organization_short_name || 'Название организации не найдено'}
        </span>
      </div>

      <div className="text-[12px] text-[#6B7280] font-medium">
        ID: {item.application_id} &nbsp;—&nbsp; {specName.split(' ').slice(0, 2).join(' ')}
      </div>

      {renderStats()}
      {renderDeadline()}
    </div>
  );
};

const KanbanBoard: React.FC<KanbanBoardProps> = ({ items, loading, onDetails }) => {
  const [completedPage, setCompletedPage] = useState(1);

  const noData = items.filter(i => i.overall_status === 'no_data');
  const inProgress = items.filter(i => i.overall_status === 'in_progress');
  const atRisk = items.filter(i => i.overall_status === 'at_risk');
  const overdue = items.filter(i => i.overall_status === 'overdue');

  const allCompleted = items
    .filter(i => i.overall_status === 'completed' || i.overall_status === 'closed')
    .sort((a, b) => b.application_id - a.application_id);

  const completedTotal = allCompleted.length;

  useEffect(() => {
    if ((completedPage - 1) * COMPLETED_PAGE_SIZE >= completedTotal) {
      setCompletedPage(1);
    }
  }, [completedTotal, completedPage]);

  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress />
      </div>
    );
  }

  const currentCompleted = allCompleted.slice(
    (completedPage - 1) * COMPLETED_PAGE_SIZE,
    completedPage * COMPLETED_PAGE_SIZE
  );

  const columns = [
    { id: 'no_data', title: 'Нет данных', data: noData },
    { id: 'in_progress', title: 'В процессе', data: inProgress },
    { id: 'at_risk', title: 'Под риском', data: atRisk },
    { id: 'overdue', title: 'Просрочено', data: overdue },
  ];

  const from = completedTotal > 0 ? (completedPage - 1) * COMPLETED_PAGE_SIZE + 1 : 0;
  const to = Math.min(completedPage * COMPLETED_PAGE_SIZE, completedTotal);

  return (
    <div className="relative flex flex-col gap-10 pt-1">
      <style>{`
        .kanban-scroll::-webkit-scrollbar { width: 4px; }
        .kanban-scroll::-webkit-scrollbar-track { background: transparent; }
        .kanban-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
        .kanban-scroll::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
      `}</style>

      {loading && (
        <div className="absolute top-[-10px] left-1/2 -translate-x-1/2 z-10 bg-white/90 p-1.5 rounded-full shadow-sm">
          <CircularProgress size={20} />
        </div>
      )}

      {/* ── Четыре колонки Канбан ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-start">
        {columns.map(col => (
          <div key={col.id} className="bg-[#F8FAFC] rounded-2xl p-3 flex flex-col gap-3 h-[650px]">
            <div className="flex items-center justify-between px-1 pt-1 pb-2 shrink-0">
              <h3 className="font-medium text-[16px] text-[#1F2937]">{col.title}</h3>
              <span className="bg-[#E2E8F0] text-[#475569] text-xs font-bold px-2 py-0.5 rounded-full">
                {col.data.length}
              </span>
            </div>

            <div className="flex flex-col gap-3 overflow-y-auto kanban-scroll pr-1 pb-2">
              {col.data.map(item => (
                <KanbanCard key={item.application_id} item={item} onClick={() => onDetails(item.application_id)} />
              ))}
              {col.data.length === 0 && (
                <div className="text-center text-[12px] text-gray-400 py-4 border-2 border-dashed border-gray-200 rounded-xl">
                  Пусто
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Таблица "Завершено" с пагинацией ── */}
      <div className="space-y-4">
        <h2 className="font-bold text-[20px] text-[#1F2937]">
          Завершено <span className="text-gray-400 text-lg ml-2">{completedTotal}</span>
        </h2>

        {completedTotal > 0 ? (
          <>
            <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
                    <th className="px-6 py-4 font-semibold text-[13px] text-[#4B5563] w-[15%]">ID-заявки</th>
                    <th className="px-6 py-4 font-semibold text-[13px] text-[#4B5563] w-[35%]">Организация</th>
                    <th className="px-6 py-4 font-semibold text-[13px] text-[#4B5563] w-[20%]">Отдел</th>
                    <th className="px-6 py-4 font-semibold text-[13px] text-[#4B5563] w-[30%]">Отв. специалист</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCompleted.map((item, idx) => (
                    <tr
                      key={item.application_id}
                      onClick={() => onDetails(item.application_id)}
                      className={`cursor-pointer hover:bg-blue-50 transition-colors ${idx !== currentCompleted.length - 1 ? 'border-b border-[#E5E7EB]' : ''}`}
                    >
                      <td className="px-6 py-4 text-[13px] text-[#6B7280]">{item.application_id}</td>
                      <td className="px-6 py-4 text-[13px] font-medium text-[#1F2937]">
                        {item.organization_short_name || 'Название организации не найдено'}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-[#6B7280]">{item.departments?.join(', ') || '—'}</td>
                      <td className="px-6 py-4 text-[13px] text-[#6B7280]">{item.specialist_full_name || 'Не назначен'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
              <div className="text-sm text-gray-700">
                Показано <span className="font-medium">{from}</span> - <span className="font-medium">{to}</span> из <span className="font-medium">{completedTotal}</span>
              </div>
              <AppPagination page={completedPage} totalItems={completedTotal} pageSize={COMPLETED_PAGE_SIZE} onChange={setCompletedPage} />
            </div>
          </>
        ) : (
          <div className="text-[13px] text-gray-500">В этой выборке нет завершенных заявок.</div>
        )}
      </div>
    </div>
  );
};

export default KanbanBoard;